import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ClipboardList, ShieldCheck, Cpu, ArrowRight, CheckCircle2 } from "lucide-react";

const DIM_ICON = { administrativo: ShieldCheck, tecnico: Cpu, plan: ClipboardList };
// Sección de la persona más relevante para resolver cada dimensión.
const DIM_TAB = { administrativo: "instrucciones", tecnico: "dispositivos", plan: "plan" };

function antiguedad(desde) {
  if (!desde) return "—";
  const ms = Date.now() - new Date(desde).getTime();
  const dias = Math.floor(ms / 86400000);
  if (dias >= 1) return `${dias} día${dias === 1 ? "" : "s"}`;
  const horas = Math.floor(ms / 3600000);
  if (horas >= 1) return `${horas} h`;
  return "hoy";
}

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—");

// v2.10.0 — Recordatorios operativos de ALTA INCOMPLETA. Superficie administrativa GLOBAL.
// Derivada 100% del backend (readiness autoritativa): el frontend no recalcula nada.
export default function Pendientes() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dimension, setDimension] = useState("todos");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get(`/recordatorios?dimension=${dimension}&estado=pendiente${q ? `&q=${encodeURIComponent(q)}` : ""}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("No se pudieron cargar los pendientes."));
  }, [dimension, q]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const revisar = (rec) => {
    const dims = rec.dimensiones || [];
    const tab = dims.length === 1 ? DIM_TAB[dims[0]] : "resumen";
    navigate(`/sitios/${rec.sitio_id}?persona=${rec.persona_id}&seccion=${tab}`);
  };

  const recs = data?.recordatorios || [];
  const res = data?.resumen || {};

  return (
    // El puente de tema nocturno (index.css · `.tema-noche-auto`) faltaba en esta raíz: el buscador,
    // el `SelectTrigger` de dimensión y la tarjeta de pendientes (`bg-white`) seguían blancos de
    // noche con texto casi blanco heredado. Misma corrección que Organizaciones y Programados.
    <div className="tema-noche-auto space-y-6" data-testid="pendientes-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#012472]">Pendientes de alta</h1>
          <p className="text-sm text-slate-500 mt-1">
            Personas activas que aún no están <b>Listas para monitoreo</b>. Derivado de la readiness del sistema.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar persona o sitio…"
            aria-label="Buscar pendiente por persona o sitio"
            className="rounded-xl bg-white w-[220px]"
            data-testid="pendientes-search"
          />
          <Select value={dimension} onValueChange={setDimension}>
            <SelectTrigger className="rounded-xl bg-white w-[200px]" aria-label="Filtrar pendientes por dimensión" data-testid="pendientes-filter-dimension"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos" data-testid="pendientes-filter-todos">Todos</SelectItem>
              <SelectItem value="administrativo" data-testid="pendientes-filter-administrativo">Administrativo</SelectItem>
              <SelectItem value="tecnico" data-testid="pendientes-filter-tecnico">Técnico/cobertura</SelectItem>
              <SelectItem value="plan" data-testid="pendientes-filter-plan">Plan de respuesta</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="pendientes-resumen">
        <span className="rounded-full bg-amber-50 text-amber-700 px-2.5 py-1 font-medium">{res.pendientes ?? 0} pendientes</span>
        <span className="rounded-full bg-slate-50 text-slate-500 px-2.5 py-1">Administrativo: {res.administrativo ?? 0}</span>
        <span className="rounded-full bg-slate-50 text-slate-500 px-2.5 py-1">Técnico: {res.tecnico ?? 0}</span>
        <span className="rounded-full bg-slate-50 text-slate-500 px-2.5 py-1">Plan: {res.plan ?? 0}</span>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
        {!data ? (
          <p className="p-6 text-sm text-slate-400">Cargando…</p>
        ) : recs.length === 0 ? (
          <p className="p-6 text-sm text-slate-500 flex items-center gap-2" data-testid="pendientes-empty">
            <CheckCircle2 strokeWidth={1.6} className="h-4 w-4 text-emerald-500" />
            Sin pendientes de alta con estos filtros.
          </p>
        ) : (
          recs.map((r) => (
            <div key={r.id} data-testid={`pendiente-row-${r.persona_id}`} className="p-4 md:p-5 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-[#012472] truncate" data-testid={`pendiente-persona-${r.persona_id}`}>{r.persona_nombre || "(sin nombre)"}</p>
                  <span className="text-xs text-slate-500" data-testid={`pendiente-sitio-${r.persona_id}`}>· {r.sitio_nombre || "(sin sitio)"}</span>
                  {(r.dimensiones || []).map((d) => {
                    const Icon = DIM_ICON[d] || ClipboardList;
                    return (
                      <span key={d} data-testid={`pendiente-dim-${r.persona_id}-${d}`} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                        <Icon strokeWidth={1.7} className="h-3 w-3" /> {d === "administrativo" ? "Administrativo" : d === "tecnico" ? "Técnico/cobertura" : "Plan de respuesta"}
                      </span>
                    );
                  })}
                </div>
                <ul className="text-xs text-slate-600 list-disc pl-5 space-y-0.5" data-testid={`pendiente-faltantes-${r.persona_id}`}>
                  {(r.faltantes_lista || []).slice(0, 4).map((f, i) => (
                    <li key={i}><span className="text-slate-400">{f.dimension_label}:</span> {f.texto}</li>
                  ))}
                  {(r.faltantes_lista || []).length > 4 && (
                    <li className="list-none -ml-5 text-slate-400">+{r.faltantes_lista.length - 4} faltante(s) más — ver en Revisar</li>
                  )}
                </ul>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="text-right">
                  <p className="text-xs font-medium text-slate-600" data-testid={`pendiente-antiguedad-${r.persona_id}`}>{antiguedad(r.pendiente_desde)}</p>
                  <p className="text-[11px] text-slate-400">desde {fecha(r.pendiente_desde)}</p>
                </div>
                <Button size="sm" variant="outline" className="rounded-lg" data-testid={`pendiente-revisar-${r.persona_id}`}
                  aria-label={`Revisar el alta de ${r.persona_nombre || "persona sin nombre"} en ${r.sitio_nombre || "sitio sin nombre"}`}
                  onClick={() => revisar(r)}>
                  Revisar <ArrowRight strokeWidth={1.6} className="h-4 w-4 ml-1" />
                  <span className="sr-only"> el alta de {r.persona_nombre || "persona sin nombre"}</span>
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
